import { Controller } from "@hotwired/stimulus";
import { PaymentCalculatorModel } from "./payment_calculator_model_v2";
import { PaymentCalculatorView } from "./payment_calculator_view_v2";

export default class extends Controller {
  static targets = [
    "homeValue",
    "currentMortgageBalance",
    "estCostsSell",
    "realtorCost",
    "estCostsSellPercentage",
    "realtorCostPercentage",
    "newHomePurchasePrice",
    "downPayment",
    "downPaymentPercentage",
    "closingCosts",
    "closingCostsPercentage",
    "realtorBuyCost",
    "realtorBuyCostPercentage",
    "loanAmount",
    "interestRate",
    "loanTerm",
    "currentHomePayment",
    "monthlyPaymentDifference",
    "cumulativeMonthlyCostDifference",
    "equityOnNewHomePayments",
    "annualAppreciation",
    "estimatedProfit",
    "estimatedCostsBuy",
    "estimatedMortgagePayment",
    "equityAfterThreeYears",
    "totalAfterThreeYears",
    "superTotal",
    "pocketSpent",
    "isCalculator",
    "newHomePurchaseText",
    "firstChartCanvas",
    "secondChartCanvas",
    "trigger",
    "content",
  ];

  connect() {
    this.model = new PaymentCalculatorModel();
    this.view = new PaymentCalculatorView();

    this.isDownPaymentManuallySet = false;
    this.isNewHomePurchasePriceManuallySet = false;
    this.isEstCostsSellManuallySet = false;
    this.isRealtorCostManuallySet = false;
    this.isClosingCostsManuallySet = false;
    this.isRealtorBuyCostManuallySet = false;
    this.isInterestRateManuallySet = false;
    this.isAnnualAppreciationManuallySet = false;

    this.model.estCostsSellPercentage = 1;
    this.model.realtorCommissionsSellPercentage = 2.5;
    this.model.estClosingCostsPercentage = 1;
    this.model.realtorCommissionsBuyPercentage = 1.5;

    this.loadInitialData();
    this.updateAll();
  }

  loadInitialData() {
    /* 
    const rawHomeValue = this.homeValueTarget.value;
    const rawMortgageBalance = this.currentMortgageBalanceTarget.value;
    const rawNewPurchasePrice = this.newHomePurchasePriceTarget.value;
    const rawInterestRate = this.interestRateTarget.value;
    const rawCurrentPayment = this.currentHomePaymentTarget.value;
    */

    this.model.homeValue = this.parseInput(this.homeValueTarget);
    this.model.currentMortgageBalance = this.parseInput(
      this.currentMortgageBalanceTarget,
    );

    this.model.newHomePurchasePrice = this.model.homeValue * 1.1;

    this.model.interestRate = this.isInterestRateManuallySet
      ? this.model.interestRate
      : this.parsePercentage(this.interestRateTarget);

    this.model.currentHomePayment = this.parseInput(
      this.currentHomePaymentTarget,
    );

    this.model.loanTermMonths = parseInt(
      this.loanTermTarget.value || "360",
      10,
    );

    this.model.annualAppreciation = this.isAnnualAppreciationManuallySet
      ? this.model.annualAppreciation
      : this.parsePercentage(this.annualAppreciationTarget) || 3.5;

    this.model.estCostsSell =
      this.model.homeValue * (this.model.estCostsSellPercentage / 100);
    this.model.realtorCommissionsSell =
      this.model.homeValue *
      (this.model.realtorCommissionsSellPercentage / 100);
    this.model.estClosingCosts =
      this.model.newHomePurchasePrice *
      (this.model.estClosingCostsPercentage / 100);
    this.model.realtorCommissionsBuy =
      this.model.newHomePurchasePrice *
      (this.model.realtorCommissionsBuyPercentage / 100);

    this.calculateDependentValues();
  }

  calculateDependentValues() {
    if (!this.isEstCostsSellManuallySet) {
      this.model.estCostsSell =
        this.model.homeValue * (this.model.estCostsSellPercentage / 100);
    }
    if (!this.isRealtorCostManuallySet) {
      this.model.realtorCommissionsSell =
        this.model.homeValue *
        (this.model.realtorCommissionsSellPercentage / 100);
    }
    if (!this.isClosingCostsManuallySet) {
      this.model.estClosingCosts =
        this.model.newHomePurchasePrice *
        (this.model.estClosingCostsPercentage / 100);
    }
    if (!this.isRealtorBuyCostManuallySet) {
      this.model.realtorCommissionsBuy =
        this.model.newHomePurchasePrice *
        (this.model.realtorCommissionsBuyPercentage / 100);
    }

    if (!this.isDownPaymentManuallySet) {
      const estimatedProfit = this.model.estimatedProfitFromSelling;
      const halfProfit = estimatedProfit * 0.5;
      const twentyPercent = this.model.newHomePurchasePrice * 0.2;
      this.model.downPayment = Math.min(halfProfit, twentyPercent);
    }

    this.model.loanAmount =
      this.model.newHomePurchasePrice - this.model.downPayment;
  }

  handleInput(event) {
    this.handleAnyInput(event);
  }

  updateMortgagePayment(event) {
    this.handleAnyInput(event);
  }

  handleAnyInput(event) {
    const el = event.target;

    if (el === this.interestRateTarget) {
      this.model.interestRate = this.parsePercentage(el);
      this.isInterestRateManuallySet = true;
    }
    if (el === this.loanTermTarget) {
      this.model.loanTermMonths = parseInt(el.value || "360", 10);
    }
    if (el === this.downPaymentTarget) {
      this.isDownPaymentManuallySet = true;
      this.model.downPayment = this.parseInput(el);
    }
    if (el === this.newHomePurchasePriceTarget) {
      this.isNewHomePurchasePriceManuallySet = true;
      this.model.newHomePurchasePrice = this.parseInput(el);
    }
    if (el === this.estCostsSellTarget) {
      this.isEstCostsSellManuallySet = true;
      this.model.estCostsSell = this.parseInput(el);
      this.updateSellingManualPercentage(el);
    }
    if (el === this.realtorCostTarget) {
      this.isRealtorCostManuallySet = true;
      this.model.realtorCommissionsSell = this.parseInput(el);
      this.updateSellingManualPercentage(el);
    }
    if (el === this.closingCostsTarget) {
      this.isClosingCostsManuallySet = true;
      this.model.estClosingCosts = this.parseInput(el);
      this.updateManualPercentage(el);
    }
    if (el === this.realtorBuyCostTarget) {
      this.isRealtorBuyCostManuallySet = true;
      this.model.realtorCommissionsBuy = this.parseInput(el);
      this.updateManualPercentage(el);
    }
    if (el === this.annualAppreciationTarget) {
      this.isAnnualAppreciationManuallySet = true;
      this.model.annualAppreciation = this.parsePercentage(el);
    }

    this.loadDataIntoModel(el);
    this.updateAll();
    this.sendTierNotification();
  }

  handleDownPaymentPercentageInput(event) {
    const perc = this.parsePercentage(event.target);
    this.model.downPayment = this.model.newHomePurchasePrice * (perc / 100);
    this.isDownPaymentManuallySet = true;
    this.updateAll();
  }

  handleClosingCostsPercentageInput(event) {
    const perc = this.parsePercentage(event.target);
    this.model.estClosingCosts = this.model.newHomePurchasePrice * (perc / 100);
    this.model.estClosingCostsPercentage = perc;
    this.isClosingCostsManuallySet = true;
    this.updateAll();
  }

  handleRealtorBuyCostPercentageInput(event) {
    const perc = this.parsePercentage(event.target);
    this.model.realtorCommissionsBuy =
      this.model.newHomePurchasePrice * (perc / 100);
    this.model.realtorCommissionsBuyPercentage = perc;
    this.isRealtorBuyCostManuallySet = true;
    this.updateAll();
  }

  handlePercentageInput(event) {
    if (event.target === this.estCostsSellPercentageTarget) {
      const perc = this.parsePercentage(event.target);
      this.model.estCostsSellPercentage = perc;
      this.model.estCostsSell = this.model.homeValue * (perc / 100);
      this.isEstCostsSellManuallySet = false;
    } else if (event.target === this.realtorCostPercentageTarget) {
      const perc = this.parsePercentage(event.target);
      this.model.realtorCommissionsSellPercentage = perc;
      this.model.realtorCommissionsSell = this.model.homeValue * (perc / 100);
      this.isRealtorCostManuallySet = false;
    }
    this.updateAll();
  }

  updateManualPercentage(sourceElement) {
    if (sourceElement === this.closingCostsTarget) {
      if (this.model.newHomePurchasePrice > 0) {
        this.model.estClosingCostsPercentage =
          (this.model.estClosingCosts / this.model.newHomePurchasePrice) * 100;
      } else {
        this.model.estClosingCostsPercentage = 0;
      }
    }
    if (sourceElement === this.realtorBuyCostTarget) {
      if (this.model.newHomePurchasePrice > 0) {
        this.model.realtorCommissionsBuyPercentage =
          (this.model.realtorCommissionsBuy / this.model.newHomePurchasePrice) *
          100;
      } else {
        this.model.realtorCommissionsBuyPercentage = 0;
      }
    }
  }

  updateSellingManualPercentage(sourceElement) {
    if (sourceElement === this.estCostsSellTarget) {
      if (this.model.homeValue > 0) {
        this.model.estCostsSellPercentage =
          (this.model.estCostsSell / this.model.homeValue) * 100;
      } else {
        this.model.estCostsSellPercentage = 0;
      }
    }
    if (sourceElement === this.realtorCostTarget) {
      if (this.model.homeValue > 0) {
        this.model.realtorCommissionsSellPercentage =
          (this.model.realtorCommissionsSell / this.model.homeValue) * 100;
      } else {
        this.model.realtorCommissionsSellPercentage = 0;
      }
    }
  }

  loadDataIntoModel(sourceElement = null) {
    const previousDownPayment = this.model.downPayment;

    this.model.homeValue = this.parseInput(this.homeValueTarget);
    this.model.currentMortgageBalance = this.parseInput(
      this.currentMortgageBalanceTarget,
    );

    if (sourceElement === this.interestRateTarget) {
      this.isInterestRateManuallySet = true;
      this.model.interestRate = this.parsePercentage(this.interestRateTarget);
    } else {
      this.model.interestRate = this.parsePercentage(this.interestRateTarget);
    }

    this.model.loanTermMonths = parseInt(
      this.loanTermTarget.value || "360",
      10,
    );
    this.model.currentHomePayment = this.parseInput(
      this.currentHomePaymentTarget,
    );

    if (sourceElement === this.annualAppreciationTarget) {
      this.isAnnualAppreciationManuallySet = true;
      this.model.annualAppreciation = this.parsePercentage(
        this.annualAppreciationTarget,
      );
    } else {
      this.model.annualAppreciation =
        this.parsePercentage(this.annualAppreciationTarget) || 3.5;
    }

    if (!this.isDownPaymentManuallySet) {
      this.model.downPayment = previousDownPayment;
    }
    if (!this.isNewHomePurchasePriceManuallySet) {
      this.model.newHomePurchasePrice = this.model.homeValue * 1.1;
    }
    if (!this.isEstCostsSellManuallySet) {
      this.model.estCostsSell =
        this.model.homeValue * (this.model.estCostsSellPercentage / 100);
    }
    if (!this.isRealtorCostManuallySet) {
      this.model.realtorCommissionsSell =
        this.model.homeValue *
        (this.model.realtorCommissionsSellPercentage / 100);
    }
    if (!this.isClosingCostsManuallySet) {
      this.model.estClosingCosts =
        this.model.newHomePurchasePrice *
        (this.model.estClosingCostsPercentage / 100);
    }
    if (!this.isRealtorBuyCostManuallySet) {
      this.model.realtorCommissionsBuy =
        this.model.newHomePurchasePrice *
        (this.model.realtorCommissionsBuyPercentage / 100);
    }

    this.calculateDependentValues();
  }

  updateAll() {
    this.calculateDependentValues();
    this.updateDisplayValues();
  }

  updateDisplayValues() {
    if (document.activeElement !== this.newHomePurchasePriceTarget) {
      this.newHomePurchasePriceTarget.value =
        PaymentCalculatorModel.formatAsCurrency(
          this.model.newHomePurchasePrice,
        );
    }
    if (document.activeElement !== this.downPaymentTarget) {
      this.downPaymentTarget.value = PaymentCalculatorModel.formatAsCurrency(
        this.model.downPayment,
      );
    }
    if (document.activeElement !== this.estCostsSellTarget) {
      this.estCostsSellTarget.value = PaymentCalculatorModel.formatAsCurrency(
        this.model.estCostsSell,
      );
    }
    if (document.activeElement !== this.realtorCostTarget) {
      this.realtorCostTarget.value = PaymentCalculatorModel.formatAsCurrency(
        this.model.realtorCommissionsSell,
      );
    }
    if (document.activeElement !== this.closingCostsTarget) {
      this.closingCostsTarget.value = PaymentCalculatorModel.formatAsCurrency(
        this.model.estClosingCosts,
      );
    }
    if (document.activeElement !== this.realtorBuyCostTarget) {
      this.realtorBuyCostTarget.value = PaymentCalculatorModel.formatAsCurrency(
        this.model.realtorCommissionsBuy,
      );
    }

    this.loanAmountTarget.value = PaymentCalculatorModel.formatAsCurrency(
      this.model.loanAmount,
    );

    this.estimatedMortgagePaymentTarget.value =
      PaymentCalculatorModel.formatAsCurrency(
        this.model.estimatedMortgagePayment,
      );
    this.estimatedProfitTarget.value = PaymentCalculatorModel.formatAsCurrency(
      this.model.estimatedProfitFromSelling,
    );
    this.estimatedCostsBuyTarget.value =
      PaymentCalculatorModel.formatAsCurrency(this.model.estimatedCostsToBuy);
    this.monthlyPaymentDifferenceTarget.value =
      PaymentCalculatorModel.formatAsCurrency(
        this.model.calcMonthlyPaymentDifference,
      );
    this.cumulativeMonthlyCostDifferenceTarget.value =
      PaymentCalculatorModel.formatAsCurrency(
        this.model.calcCumulativeMonthlyCostDifference,
      );
    this.equityOnNewHomePaymentsTarget.value =
      PaymentCalculatorModel.formatAsCurrency(
        this.model.calcEquityOnNewHomePayments,
      );

    this.totalAfterThreeYearsTarget.textContent =
      PaymentCalculatorModel.formatAsCurrency(this.model.totalCashDifference);
    this.equityAfterThreeYearsTarget.textContent =
      PaymentCalculatorModel.formatAsCurrency(this.model.equityInNewHome);
    this.superTotalTarget.textContent = PaymentCalculatorModel.formatAsCurrency(
      this.model.totalCashAndEquity,
    );

    if (this.model.totalCashDifference >= 0) {
      this.pocketSpentTarget.textContent =
        this.isCalculatorTarget.value === "true" ? "pocketed" : "pocket";
    } else {
      this.pocketSpentTarget.textContent =
        this.isCalculatorTarget.value === "true" ? "spent" : "spend";
    }

    this.newHomePurchaseTextTarget.textContent =
      PaymentCalculatorModel.formatAsCurrency(this.model.newHomePurchasePrice);

    if (
      this.hasEstCostsSellPercentageTarget &&
      document.activeElement !== this.estCostsSellPercentageTarget
    ) {
      this.estCostsSellPercentageTarget.value = `${this.model.estCostsSellPercentage.toFixed(
        1,
      )}%`;
    }
    if (
      this.hasRealtorCostPercentageTarget &&
      document.activeElement !== this.realtorCostPercentageTarget
    ) {
      this.realtorCostPercentageTarget.value = `${this.model.realtorCommissionsSellPercentage.toFixed(
        1,
      )}%`;
    }
    if (
      this.hasDownPaymentPercentageTarget &&
      document.activeElement !== this.downPaymentPercentageTarget
    ) {
      let computedPercentage = 0;
      if (this.model.newHomePurchasePrice > 0) {
        computedPercentage =
          (this.model.downPayment / this.model.newHomePurchasePrice) * 100;
      }
      this.downPaymentPercentageTarget.value = `${computedPercentage.toFixed(
        1,
      )}%`;
    }
    if (
      this.hasClosingCostsPercentageTarget &&
      document.activeElement !== this.closingCostsPercentageTarget
    ) {
      this.closingCostsPercentageTarget.value = `${this.model.estClosingCostsPercentage.toFixed(
        1,
      )}%`;
    }
    if (
      this.hasRealtorBuyCostPercentageTarget &&
      document.activeElement !== this.realtorBuyCostPercentageTarget
    ) {
      this.realtorBuyCostPercentageTarget.value = `${this.model.realtorCommissionsBuyPercentage.toFixed(
        1,
      )}%`;
    }

    if (this.hasFirstChartCanvasTarget) {
      this.view.updateFirstChart(this.firstChartCanvasTarget, this.model);
    }
    if (this.hasSecondChartCanvasTarget) {
      this.view.updateSecondChart(this.secondChartCanvasTarget, this.model);
    }
  }

  toggle(event) {
    event.preventDefault();
    const trigger = event.currentTarget;
    const content = trigger.nextElementSibling;
    this.view.toggleSection(trigger, content);
  }

  disconnect() {
    if (this.view.firstChart) {
      this.view.firstChart.destroy();
      this.view.firstChart = null;
    }
    if (this.view.secondChart) {
      this.view.secondChart.destroy();
      this.view.secondChart = null;
    }
  }

  parseInput(element) {
    if (!element) return 0;
    const raw = PaymentCalculatorModel.stripFormatting(element.value || "");
    const parsed = parseFloat(raw) || 0;
    return parsed;
  }

  parsePercentage(element) {
    if (!element) return 0;
    const val = element.value.replace(/%/g, "");
    const num = parseFloat(val);
    return isNaN(num) ? 0 : num;
  }

  formatCurrency(event) {
    const input = event.target;
    const raw = PaymentCalculatorModel.stripFormatting(input.value);
    input.value = PaymentCalculatorModel.formatAsCurrency(raw);
  }

  formatPercentage(event) {
    const input = event.target;
    const val = input.value.replace(/%/g, "");
    let num = parseFloat(val);
    if (isNaN(num)) num = 0;
    input.value = `${num.toFixed(1)}%`;
  }

  debounce(func, delay) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  }

  sendTierNotification = this.debounce(() => {
    fetch("/value_report/track_event", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        tn_id: 26,
        event: "calculator_interaction",
        timestamp: Date.now(),
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.headers
          .get("content-type")
          ?.includes("application/json")
          ? await response.json()
          : null;
      })
      .catch(() => {});
  }, 500);
}
